import React, { createContext, useContext, useState, useEffect } from 'react';
import config from '../components/common/Config';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRolesFromStorage();
    fetchUserRolesAndPermissions();
    
    const activeRoleId = localStorage.getItem('active_role_id');
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');
    const token = localStorage.getItem('token');
    
    if (activeRoleId && userId && accessToken) {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('role_id', activeRoleId);
      
      fetch(`${config.API_URL}/get_user_roles_with_permissions`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data && data.data.permissions) {
          const permissionsMap = data.data.permissions || {};
          setRolePermissions(prev => ({ ...prev, ...permissionsMap }));
          localStorage.setItem('role_permissions_map', JSON.stringify({ ...JSON.parse(localStorage.getItem('role_permissions_map') || '{}'), ...permissionsMap }));
          
          if (permissionsMap[activeRoleId]) {
            localStorage.setItem('user_permissions', JSON.stringify(permissionsMap[activeRoleId]));
          }
        }
      })
      .catch(error => {
        console.error('Error fetching permissions for active role:', error);
      });
    }

    const handleUserLogin = () => {
      setTimeout(() => {
        loadRolesFromStorage();
        fetchUserRolesAndPermissions();
      }, 100);
    };

    window.addEventListener('userLoggedIn', handleUserLogin);

    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin);
    };
  }, []);

  const loadRolesFromStorage = () => {
    try {
      const storedRoles = localStorage.getItem('user_roles_data');
      const storedActiveRole = localStorage.getItem('active_role_id');
      const storedPermissions = localStorage.getItem('role_permissions_map');

      if (storedRoles) {
        const roles = JSON.parse(storedRoles);
        setUserRoles(roles);
        
        if (storedPermissions) {
          setRolePermissions(JSON.parse(storedPermissions));
        }

        if (storedActiveRole && roles.length > 0) {
          const active = roles.find(r => r.id == storedActiveRole || r.id === storedActiveRole) || roles[0];
          setActiveRole(active);
          updateLocalStorageRole(active.id);
        } else if (roles.length > 0) {
          const defaultRole = roles[0];
          setActiveRole(defaultRole);
          updateLocalStorageRole(defaultRole.id);
        }
      } else {
        const currentRoleId = localStorage.getItem('user_role');
        if (currentRoleId) {
          const singleRole = { id: currentRoleId, name: `Role ${currentRoleId}` };
          setUserRoles([singleRole]);
          setActiveRole(singleRole);
          updateLocalStorageRole(currentRoleId);
        }
      }
    } catch (error) {
      console.error('Error loading roles from storage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserRolesAndPermissions = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!accessToken || !userId) {
      setIsLoading(false);
      return;
    }

    try {
      // Use get_users_role_for_permission API to fetch roles for dashboard dropdown
      // API requires userId as POST parameter
      const formData = new FormData();
      formData.append('userId', userId);

      const response = await fetch(`${config.API_URL}/get_users_role_for_permission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      console.log(config.API_URL);
      const data = await response.json();
      console.log('API Response - get_users_role_for_permission:', data);
      
      // Handle 404 - No roles found
      if (response.status === 404) {
        console.warn('No roles found for user:', data.message);
        // Clear roles and don't show dropdown
        setUserRoles([]);
        setIsLoading(false);
        
        return;
      }
      
      // Handle successful response
      if (response.ok) {
        // Check if API returns roles and permissions together
        if (data.success && data.data) {
          let formattedRoles = [];
          
          // Handle different response structures
          if (data.data.roles && Array.isArray(data.data.roles) && data.data.roles.length > 0) {
            // Format roles from API response (role_id and name fields)
            formattedRoles = data.data.roles.map(item => ({
              id: (item.role_id || item.id).toString(),
              name: item.name || `Role ${item.role_id || item.id}`
            }));
          } else if (Array.isArray(data.data) && data.data.length > 0) {
            // Legacy format: data is directly an array
            formattedRoles = data.data.map(item => ({
              id: (item.role_id || item.id).toString(),
              name: item.name || `Role ${item.role_id || item.id}`
            }));
          }
          
          console.log('Roles from API:', formattedRoles);
          console.log('Number of roles:', formattedRoles.length);
          
          // Handle permissions if included in the same response
          if (data.data.permissions && typeof data.data.permissions === 'object') {
            const permissionsMap = data.data.permissions;
            console.log('Permissions from API:', permissionsMap);
            setRolePermissions(permissionsMap);
            localStorage.setItem('role_permissions_map', JSON.stringify(permissionsMap));
            
            // Update user_permissions for active role
            const currentActiveRoleId = localStorage.getItem('active_role_id') || (formattedRoles.length > 0 ? formattedRoles[0].id : null);
            if (currentActiveRoleId && permissionsMap[currentActiveRoleId]) {
              localStorage.setItem('user_permissions', JSON.stringify(permissionsMap[currentActiveRoleId]));
            }
          }
          
          if (formattedRoles.length > 0) {
            setUserRoles(formattedRoles);
            
            // Store roles in localStorage
            localStorage.setItem('user_roles_data', JSON.stringify(formattedRoles));
            
            // Set active role if not already set
            const currentActiveRoleId = localStorage.getItem('active_role_id');
            if (currentActiveRoleId && formattedRoles.length > 0) {
              const active = formattedRoles.find(r => r.id.toString() === currentActiveRoleId.toString()) || formattedRoles[0];
              if (active) {
                setActiveRole(active);
                updateLocalStorageRole(active.id);
                // Update permissions for active role
                if (data.data.permissions && data.data.permissions[active.id]) {
                  localStorage.setItem('user_permissions', JSON.stringify(data.data.permissions[active.id]));
                }
              }
            } else if (formattedRoles.length > 0) {
              // Set first role as active if no active role is set
              const defaultRole = formattedRoles[0];
              setActiveRole(defaultRole);
              updateLocalStorageRole(defaultRole.id);
              // Update permissions for default role
              if (data.data.permissions && data.data.permissions[defaultRole.id]) {
                localStorage.setItem('user_permissions', JSON.stringify(data.data.permissions[defaultRole.id]));
              }
            }
             
            // Only fetch permissions separately if not already included in response
            if (!data.data.permissions) {
              const activeRoleId = localStorage.getItem('active_role_id') || (formattedRoles.length > 0 ? formattedRoles[0].id : null);
              fetchUserPermissionsForRoles(formattedRoles, accessToken, userId, activeRoleId);
            }
          } else {
            console.warn('API response not successful or missing roles data:', data);
            // Clear roles and don't show dropdown
            setUserRoles([]);
          }
        } else {
          console.warn('API response not successful or missing roles data:', data);
          // Clear roles and don't show dropdown
          setUserRoles([]);
        }
      } else {
        console.error('API request failed:', response.status, response.statusText);
        console.error('Error details:', data);
        // Clear roles on error
        setUserRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch permissions for user's roles separately
  const fetchUserPermissionsForRoles = async (roles, token, userId, specificRoleId = null) => {
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      
      if (specificRoleId) {
        formData.append('role_id', specificRoleId);
      }
      
      const response = await fetch(`${config.API_URL}/get_user_roles_with_permissions`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.permissions) {
          const permissionsMap = data.data.permissions || {};
          setRolePermissions(permissionsMap);
          localStorage.setItem('role_permissions_map', JSON.stringify(permissionsMap));
          
          if (specificRoleId && permissionsMap[specificRoleId]) {
            localStorage.setItem('user_permissions', JSON.stringify(permissionsMap[specificRoleId]));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const switchRole = async (roleId) => {
    const role = userRoles.find(r => r.id === roleId || r.id.toString() === roleId.toString());
    if (role) {
      setActiveRole(role);
      updateLocalStorageRole(roleId);
      localStorage.setItem('active_role_id', roleId);
      
      const accessToken = localStorage.getItem('accessToken');
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      if (accessToken && userId) {
        try {
          const formData = new FormData();
          formData.append('user_id', userId);
          formData.append('role_id', roleId);
          
          const response = await fetch(`${config.API_URL}/get_user_roles_with_permissions`, {
            method: 'POST',
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          });

          const data = await response.json();
          
          if (response.ok && data.success && data.data && data.data.permissions) {
            const permissionsMap = data.data.permissions || {};
            setRolePermissions(prev => ({ ...prev, ...permissionsMap }));
            const updatedPermissionsMap = { ...rolePermissions, ...permissionsMap };
            localStorage.setItem('role_permissions_map', JSON.stringify(updatedPermissionsMap));
            
            const permissions = permissionsMap[roleId] || [];
            if (permissions.length > 0) {
              localStorage.setItem('user_permissions', JSON.stringify(permissions));
            } else {
              const cachedPermissions = rolePermissions[roleId] || [];
              localStorage.setItem('user_permissions', JSON.stringify(cachedPermissions));
            }
          } else {
            const formDataAll = new FormData();
            formDataAll.append('user_id', userId);
            
            const responseAll = await fetch(`${config.API_URL}/get_user_roles_with_permissions`, {
              method: 'POST',
              headers: {
                authorization: `Bearer ${accessToken}`,
              },
              body: formDataAll,
            });

            if (responseAll.ok) {
              const dataAll = await responseAll.json();
              if (dataAll.success && dataAll.data && dataAll.data.permissions) {
                const permissionsMap = dataAll.data.permissions || {};
                setRolePermissions(permissionsMap);
                localStorage.setItem('role_permissions_map', JSON.stringify(permissionsMap));
                
                const permissions = permissionsMap[roleId] || [];
                localStorage.setItem('user_permissions', JSON.stringify(permissions));
              } else {
                const permissions = rolePermissions[roleId] || [];
                localStorage.setItem('user_permissions', JSON.stringify(permissions));
              }
            } else {
              const permissions = rolePermissions[roleId] || [];
              localStorage.setItem('user_permissions', JSON.stringify(permissions));
            }
          }
        } catch (error) {
          console.error('Error fetching permissions for role:', error);
          const permissions = rolePermissions[roleId] || [];
          localStorage.setItem('user_permissions', JSON.stringify(permissions));
        }
      } else {
        const permissions = rolePermissions[roleId] || [];
        localStorage.setItem('user_permissions', JSON.stringify(permissions));
      }
      
      window.dispatchEvent(new CustomEvent('roleChanged', { detail: { roleId, role } }));
    }
  };

  const updateLocalStorageRole = (roleId) => {
    localStorage.setItem('user_role', roleId);
  };

  const getActiveRolePermissions = () => {
    if (!activeRole) return [];
    return rolePermissions[activeRole.id] || [];
  };

  const hasPermission = (permission) => {
    const permissions = getActiveRolePermissions();
    // Check for exact match first
    if (permissions.includes(permission)) {
      return true;
    }
    // Also check for label/ID match (e.g., "HoldList" vs "Hold List")
    // This handles cases where API returns labels but we check with IDs, or vice versa
    try {
      const modulesConfig = require('../components/common/modulesConfig').default;
      for (const module of modulesConfig) {
        for (const perm of module.permissions) {
          // If checking with ID, also check if label exists in permissions
          if (perm.id === permission && permissions.includes(perm.label)) {
            return true;
          }
          // If checking with label, also check if ID exists in permissions
          if (perm.label === permission && permissions.includes(perm.id)) {
            return true;
          }
        }
      }
    } catch (error) {
      // If modulesConfig import fails, just return false
    }
    return false;
  };

  const hasAnyPermission = (permissionList) => {
    const permissions = getActiveRolePermissions();
    return permissionList.some(perm => permissions.includes(perm));
  };

  const hasAllPermissions = (permissionList) => {
    const permissions = getActiveRolePermissions();
    return permissionList.every(perm => permissions.includes(perm));
  };

  const isRole = (roleId) => {
    return activeRole && activeRole.id == roleId;
  };

  const value = {
    userRoles,
    activeRole,
    rolePermissions,
    isLoading,
    switchRole,
    getActiveRolePermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isRole,
    fetchUserRolesAndPermissions,
    refreshRoles: fetchUserRolesAndPermissions,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

